// TRIAGE-01 / TRIAGE-02 (audit decision 3.2 — IMP-01/02/05) — the global
// triage hotkey layer.
//
// Mounted ONCE per window in `AnalysisLayoutMount` (next to
// `useUndoHotkey`), so pop-out windows get their own listener and the
// selection sync rides the existing BroadcastChannel stores.
//
// Dispatch guards, in order (every one is unit-tested):
//   1. `e.defaultPrevented` — another layer (Viewer Space-pan, DrawOverlay
//      d/r/p/l, escape stack) already claimed the press.
//   2. chord normalisation — Ctrl/Cmd/Alt chords are never triage chords.
//   3. typing targets — INPUT / TEXTAREA / SELECT / contentEditable.
//   4. open MUI overlay — dialogs, menus, popovers, listboxes (the
//      Settings modal, the DefectDrawer, the dismiss popover…).
//   5. Viewer-claimed Space — the shared Viewer primitive owns
//      Space-hold-pan while the pointer/focus is on the canvas; the
//      triage Space binding yields there (matches the Viewer's own
//      hover/focus-within claim check, listener-order independent).
//   6. interactive focus for activation chords (Enter / Space / Delete)
//      — a focused button keeps its native activation semantics.
//   7. draw mode (`drawStore.enabled`) suspends ALL triage keys
//      (IMP-01 edge case — DrawOverlay owns the keyboard there).
//   8. readiness — `isGradingReady()` gates everything (decisions AND
//      navigation; the queue's data isn't trustworthy before
//      `dataPointReady` anyway).
//   9. decisions additionally require `editAllowed` (D-5 gate);
//      navigation still works on read-only/locked DAs.
//
// PR 1 wires the navigation actions (j/k finding cursor, Space /
// Shift+Space queue jumps). Decision actions (accept / dismiss /
// severity) dispatch through the optional `decisions` handler bundle —
// absent (PR 1) they no-op without consuming the key.
import { useEffect, useRef } from "react";
import { useDrawStore } from "../state/drawStore.js";
import { isGradingReady } from "../state/analysisReadinessStore.js";
import { useImageActivationStore } from "../state/imageActivationStore.js";
import { useSelectionStore } from "../state/selectionStore.js";
import { useTriagePreferencesStore } from "./triagePreferencesStore.js";
import {
  eventToChord,
  resolveTriageAction,
  type TriageActionId,
} from "./keymap.js";
import {
  usePendingFindingCursor,
  type PendingFindingCursor,
} from "./usePendingFindingCursor.js";
import { useTriageQueue, type TriageQueue } from "./useTriageQueue.js";

/** Severity grade for the `1`..`5` actions. */
export type SeverityGrade = 1 | 2 | 3 | 4 | 5;

/**
 * Decision dispatch handlers (TRIAGE-02). The hotkey layer resolves the
 * ACTIVE pending AI finding itself and passes its id; severity passes
 * the grade (defect semantics live with the handler).
 */
export interface TriageDecisionHandlers {
  accept: (findingId: string) => void;
  dismiss: (findingId: string) => void;
  dismissWithReason: (findingId: string) => void;
  setSeverity: (grade: SeverityGrade) => void;
}

export interface UseTriageHotkeysArgs {
  dataAcquisitionId: string;
  /** D-5 gate — decisions no-op when false; navigation still works. */
  editAllowed: boolean;
  /** TRIAGE-02 decision handlers; absent ⇒ decision keys no-op. */
  decisions?: TriageDecisionHandlers | undefined;
}

const SEVERITY_BY_ACTION: Partial<Record<TriageActionId, SeverityGrade>> = {
  severity1: 1,
  severity2: 2,
  severity3: 3,
  severity4: 4,
  severity5: 5,
};

/** Activation-semantics chords that must yield to a focused control. */
function isActivationChord(chord: string): boolean {
  return (
    chord === "Enter" ||
    chord === "Space" ||
    chord === "Shift+Space" ||
    chord === "Delete"
  );
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return (
    tag === "INPUT" ||
    tag === "TEXTAREA" ||
    tag === "SELECT" ||
    target.isContentEditable
  );
}

function isInsideOverlay(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.closest(
      '[role="dialog"], [role="menu"], [role="listbox"], .MuiModal-root, .MuiPopover-root',
    ) !== null
  );
}

function isInteractiveElement(el: Element | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  if (el.tagName === "BUTTON" || el.tagName === "A" || el.tagName === "SELECT")
    return true;
  const role = el.getAttribute("role");
  return (
    role === "button" ||
    role === "tab" ||
    role === "menuitem" ||
    role === "option" ||
    role === "checkbox" ||
    role === "radio" ||
    role === "link"
  );
}

/**
 * Mirrors the shared Viewer primitive's Space-pan claim condition
 * (packages/ui Viewer.tsx — hover OR focus-within on the OSD wrapper),
 * so the triage Space binding yields regardless of listener order.
 */
function viewerClaimsSpace(): boolean {
  if (typeof document === "undefined") return false;
  const wrappers = document.querySelectorAll<HTMLElement>(
    '[data-testid="viewer-wrapper"]',
  );
  for (const wrapper of wrappers) {
    if (
      wrapper.contains(document.activeElement) ||
      wrapper === document.activeElement ||
      wrapper.matches(":hover")
    ) {
      return true;
    }
  }
  return false;
}

export function useTriageHotkeys({
  dataAcquisitionId,
  editAllowed,
  decisions,
}: UseTriageHotkeysArgs): void {
  const cursor = usePendingFindingCursor(dataAcquisitionId);
  const queue = useTriageQueue(dataAcquisitionId);

  // Refs so the single window listener always sees the latest handles
  // without re-binding on every render (same pattern as useUndoHotkey).
  const refs = useRef<{
    cursor: PendingFindingCursor;
    queue: TriageQueue;
    editAllowed: boolean;
    decisions: TriageDecisionHandlers | undefined;
  }>({ cursor, queue, editAllowed, decisions });
  refs.current = { cursor, queue, editAllowed, decisions };

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.defaultPrevented) return;
      if (e.repeat && (e.key === "Enter" || e.key === " ")) return;
      const chord = eventToChord(e);
      if (chord === null) return;
      if (isTypingTarget(e.target)) return;
      if (isInsideOverlay(e.target)) return;

      const { keymap } = useTriagePreferencesStore.getState();
      const action = resolveTriageAction(keymap, chord);
      if (action === null) return;

      // A focused button/link keeps native Enter/Space/Delete semantics.
      if (
        isActivationChord(chord) &&
        (isInteractiveElement(document.activeElement) ||
          isInteractiveElement(e.target instanceof Element ? e.target : null))
      ) {
        return;
      }
      // The Viewer's Space-hold pan owns Space on the canvas.
      if (
        (chord === "Space" || chord === "Shift+Space") &&
        viewerClaimsSpace()
      ) {
        return;
      }
      // Draw mode owns the keyboard (IMP-01 edge case).
      if (useDrawStore.getState().enabled) return;
      // Dataset not ready — every triage key no-ops (PROG-01 gate).
      if (!isGradingReady()) return;

      const r = refs.current;
      switch (action) {
        case "nextFinding":
          e.preventDefault();
          r.cursor.selectNext();
          return;
        case "prevFinding":
          e.preventDefault();
          r.cursor.selectPrevious();
          return;
        case "nextPendingImage":
          e.preventDefault();
          r.queue.goToNextPendingImage();
          return;
        case "nextUnseenImage":
          e.preventDefault();
          r.queue.goToNextUnseenImage();
          return;
        case "accept":
        case "dismiss":
        case "dismissWithReason": {
          if (!r.editAllowed || !r.decisions) return;
          // Resolve the active PENDING AI finding on the active image.
          const { activeFindingId, activeKind } = useSelectionStore.getState();
          if (activeFindingId === null || activeKind === "defect") return;
          const finding = r.cursor.findings.find(
            (f) => f.id === activeFindingId,
          );
          if (finding?.status !== "pending") return;
          e.preventDefault();
          if (action === "accept") r.decisions.accept(activeFindingId);
          else if (action === "dismiss") r.decisions.dismiss(activeFindingId);
          else r.decisions.dismissWithReason(activeFindingId);
          return;
        }
        default: {
          const grade = SEVERITY_BY_ACTION[action];
          if (grade === undefined) return;
          if (!r.editAllowed || !r.decisions) return;
          e.preventDefault();
          r.decisions.setSeverity(grade);
          return;
        }
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  // ── Auto-select on activation (IMP-01 / UX-08 half) ────────────────
  // When the active image changes and the image has pending findings,
  // select the first one — but:
  //   * only after the user has interacted once (page-load default
  //     activation must not fire side effects — the
  //     useFocusEqualsReviewEffect gate pattern),
  //   * never stomp an explicit selection that already belongs to the
  //     new image (a tile click selects the finding AND activates its
  //     image — the click wins).
  const hasInteractedRef = useRef(false);
  useEffect(() => {
    const flip = () => {
      hasInteractedRef.current = true;
    };
    document.addEventListener("pointerdown", flip, {
      capture: true,
      once: true,
    });
    document.addEventListener("keydown", flip, { capture: true, once: true });
    return () => {
      document.removeEventListener("pointerdown", flip, { capture: true });
      document.removeEventListener("keydown", flip, { capture: true });
    };
  }, []);

  const activeImageId = useImageActivationStore((s) => s.activeImageId);
  const lastAutoSelectedImageRef = useRef<string | null>(null);
  useEffect(() => {
    if (!activeImageId) return;
    if (lastAutoSelectedImageRef.current === activeImageId) return;
    if (!hasInteractedRef.current) {
      // Remember the page-load default image so a later interaction
      // doesn't retroactively auto-select on it.
      lastAutoSelectedImageRef.current = activeImageId;
      return;
    }
    // Wait until this image's effective findings have arrived — the
    // cursor's findings are image-scoped, so a stale (previous image)
    // payload must not drive the selection.
    if (cursor.findings.length === 0) return;
    if (cursor.findings.some((f) => f.imageId !== activeImageId)) return;
    lastAutoSelectedImageRef.current = activeImageId;
    const { activeFindingId } = useSelectionStore.getState();
    const selectionOnImage =
      activeFindingId !== null &&
      cursor.findings.some((f) => f.id === activeFindingId);
    if (selectionOnImage) return; // explicit selection wins
    cursor.selectFirstPending();
  }, [activeImageId, cursor]);
}
